import React, { FC, MouseEvent, useEffect, useRef } from 'react'
import { Tour } from '../Popover.types'

import '../css/FocusAreaDialog.scss'

export type FocusAreaDialogProps = {
  position: string
  children: React.ReactNode
  tour: Tour
  focusAreaDialogCloseButtonRef: React.RefObject<HTMLButtonElement>
  active: boolean
  index: number
}

/**
 * The FocusAreaDialog component displays a popup dialog that gives context for a focus area.
 */
const FocusAreaDialog: FC<FocusAreaDialogProps> = props => {
  const {
    active,
    position,
    focusAreaDialogCloseButtonRef,
    children,
    index,
    tour: { setCurrentFocusArea, hasPrevious, hasNext },
  } = props
  const focusButton = useRef<HTMLButtonElement>(null)
  const focusModal = useRef<HTMLDivElement>(null)
  const previousButton = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!active || !focusAreaDialogCloseButtonRef?.current) return
    focusAreaDialogCloseButtonRef.current.focus()
  }, [active])

  useEffect(() => {
    const focusKeyboardTrap = (e: KeyboardEvent) => {
      const modal = focusModal.current
      if (!modal) {
        return
      }
      let isTabPressed = e.key === 'Tab'
      if (!isTabPressed) {
        return
      }
      if (e.shiftKey) {
        // if shift key pressed for shift + tab combination
        if (document.activeElement === focusAreaDialogCloseButtonRef.current) {
          e.preventDefault()
          if (previousButton.current?.disabled) {
            // first annotation on page has button disabled
            focusButton.current?.focus()
          } else {
            previousButton.current?.focus() // if focus is at top right close button go to previous button
          }
        }
      } else {
        // if tab key is pressed
        if (
          document.activeElement === previousButton.current ||
          (document.activeElement === focusButton.current && previousButton.current?.disabled)
        ) {
          // if focus is at previous button go to upper right close button
          focusAreaDialogCloseButtonRef.current?.focus()
          e.preventDefault()
        }
      }
    }

    document.addEventListener('keydown', focusKeyboardTrap)
    return () => {
      document.removeEventListener('keydown', focusKeyboardTrap)
    }
  }, [])

  const handleNextClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setCurrentFocusArea(index + 1)
  }
  const handlePreviousClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setCurrentFocusArea(index - 1)
  }
  const handleCloseClick = () => {
    setCurrentFocusArea(null)
  }

  return (
    <div
      className='FocusAreaDialog'
      ref={focusModal}
      data-position={position}
      role='dialog'
      aria-hidden={!active}
      aria-label='Content'>
      <button
        ref={focusAreaDialogCloseButtonRef}
        type='button'
        onClick={handleCloseClick}
        className='FocusAreaDialogClose Button Button--Tertiary Button--small'>
        <span aria-hidden='true' className='Icon u-textSizeMsrp2'>
          &#xe5cd;
        </span>
        <span className='u-hiddenVisually'>Close</span>
      </button>
      <div className='Content u-padding5gu'>{children}</div>
      <div className='FocusAreaDialogButtons u-flex u-flexRow u-padding5gu u-sizeFull'>
        {hasNext ? (
          <button
            type='button'
            onClick={handleNextClick}
            ref={focusButton}
            className='Button Button--primary Button--focus Button--small'>
            Next
          </button>
        ) : (
          <button
            type='button'
            onClick={handleCloseClick}
            ref={focusButton}
            className='Button Button--primary Button--focus Button--small'>
            Close
          </button>
        )}
        <button
          type='button'
          onClick={handlePreviousClick}
          ref={previousButton}
          className='Button Button--secondary Button--small u-marginRight3gu'
          disabled={!hasPrevious}>
          Previous
        </button>
      </div>
    </div>
  )
}

export default FocusAreaDialog
